import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {UserRoleManagementView} from 'src/modules/admin/user-role-management/user-role-management.view';
import {updateUserRole} from 'src/modules/admin/user-role-management/utils/updateUserRole';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getAllUserRoles,
    getAllUserRolesSuccess,
    saveUserRoles, saveUserRolesFailure,
    saveUserRolesSuccess,
} from 'src/openapi/apis/UserRoleControllerApi';
import {User} from 'src/openapi/models/User';
import {UserRoles} from 'src/openapi/models/UserRoles';
import {useAppDispatch} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

const SUCCESS_AUTO_HIDE_TIMEOUT = 2000;
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const UserRoleManagementController = () => {
    const {onCreateNote} = usePushNotification();
    const dispatch = useAppDispatch();
    const [userRolesData, setUserRolesData] = useState<UserRoles[]>([]);
    const userEmails = useMemo(() => {
        return [...new Set(userRolesData.map(userRoles => userRoles.email))];
    }, [userRolesData]);

    useEffect(() => {
        dispatch(getAllUserRoles()).then(e => {
            if (!e.payload.error && e.type === getAllUserRolesSuccess.type) {
                setUserRolesData(e.payload.data);
            }
        });
    }, [dispatch]);

    const onRemoveHandle = (email: string) => {
        setUserRolesData(prev => prev.filter(userRoles => userRoles.email !== email));
    };

    const onSaveHandle = useCallback(async (userRoles: UserRoles) => {
        const response = await dispatch(saveUserRoles({userRoles}));

        if (response.type === saveUserRolesSuccess.type && !response.payload?.error) {
            onCreateNote({
                id: saveUserRolesSuccess.type,
                status: Status.SUCCESS,
                message: 'Saved',
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
        }

        if (response.type === saveUserRolesFailure.type && response.payload?.error) {
            onCreateNote({
                id: saveUserRolesFailure.type,
                status: Status.ERROR,
                message: response.payload?.data?.message ?? 'Unknown error',
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    }, [dispatch, onCreateNote]);

    const onRoleChangeHandle = (email: string, role: string, checked: boolean) => {
        setUserRolesData(prev => prev.map(userRoles =>
            userRoles.email === email ? updateUserRole(userRoles, role, checked) : userRoles));
    };

    const onSelectedUser = (user: User) => {
        const newUser: UserRoles = {
            email: user.email,
            roles: [],
        };

        setUserRolesData(prev => [...prev, newUser]);
    };

    return (
        <>
            <UsersInfoController emails={userEmails}/>
            <UserRoleManagementView
                userRolesData={userRolesData}
                onRemoveHandle={onRemoveHandle}
                onSaveHandle={onSaveHandle}
                onRoleChangeHandle={onRoleChangeHandle}
                onSelectedUser={onSelectedUser}
            />
        </>
    );
};
